import { Snackbar, Slide, SlideProps, Alert, AlertColor, Typography, Box } from "@mui/material";
import { PendingActionType, PendingActionSummary } from "../types/pendingActions";

const SlideTransition = (props: SlideProps) => {
  return <Slide {...props} direction="up" />;
};

const getActionTypeLabel = (type: PendingActionType): string => {
  const labels: Record<PendingActionType, string> = {
    policy: "Policy",
    user: "User",
    transaction: "Transaction",
    group: "Group",
    vote: "Vote",
    account_setup: "Safe",
    helper: "Helper",
  };
  return labels[type] || "Action";
};

const getSyncedDetail = (
  actionType: PendingActionType,
  summary: PendingActionSummary
): { title: string; detail?: string } => {
  switch (actionType) {
    case "transaction":
      return {
        title: "Transaction confirmed",
        detail:
          summary.amount && summary.asset
            ? `Sent ${summary.amount} ${summary.asset}${summary.recipientAddress ? ` to ${summary.recipientAddress.slice(0, 6)}...${summary.recipientAddress.slice(-4)}` : ""}`
            : summary.description,
      };
    case "policy":
      return {
        title: `Policy ${summary.policyName} is synced`,
        detail: summary.policyName
          ? `"${summary.policyName}"${summary.policyType ? ` (${summary.policyType})` : ""}`
          : summary.description,
      };
    case "user":
      return {
        title: `User ${summary.userName} update confirmed`,
        detail: summary.userName
          ? `${summary.userName}${summary.userAddress ? ` (${summary.userAddress.slice(0, 6)}...${summary.userAddress.slice(-4)})` : ""}`
          : summary.description,
      };
    case "vote":
      return {
        title: "Vote submitted",
        detail: summary.voteChoice
          ? `Voted ${summary.voteChoice}${summary.targetActionType ? ` on ${summary.targetActionType}` : ""}`
          : summary.description,
      };
    case "account_setup":
      return {
        title: "Your safe is ready!",
        detail: summary.accountName || summary.description,
      };
    case "helper":
      return {
        title: "Helper update confirmed",
        detail: summary.helperName
          ? `${summary.helperAction === "remove" ? "Removed" : summary.helperAction === "update" ? "Updated" : "Added"} ${summary.helperName}${summary.helperAddress ? ` (${summary.helperAddress.slice(0, 6)}...${summary.helperAddress.slice(-4)})` : ""}`
          : summary.description,
      };
    case "group":
      return {
        title: "Group update confirmed",
        detail: summary.groupName ? `"${summary.groupName}"` : summary.description,
      };
    default:
      return { title: "Action confirmed", detail: summary.description };
  }
};

const getExpiredDetail = (
  actionType: PendingActionType,
  summary: PendingActionSummary
): { title: string; detail?: string } => {
  const label = getActionTypeLabel(actionType);
  switch (actionType) {
    case "transaction":
      return {
        title: "Transaction timed out",
        detail:
          summary.amount && summary.asset
            ? `${summary.amount} ${summary.asset} transfer was not confirmed yet. Please try again.`
            : `${label} request was not confirmed. Please try again.`,
      };
    case "vote":
      return {
        title: "Vote timed out",
        detail: summary.voteChoice
          ? `Your ${summary.voteChoice} vote was not confirmed. Please try again.`
          : "Your vote was not confirmed. Please try again.",
      };
    default:
      return {
        title: `${label} timed out`,
        detail: summary.description
          ? `${summary.description} — please try again.`
          : `Your ${label.toLowerCase()} request was not confirmed. Please try again.`,
      };
  }
};

interface PendingActionSnackbarProps {
  open: boolean;
  onClose: () => void;
  type: "synced" | "expired";
  actionType: PendingActionType;
  summary: PendingActionSummary;
}

const PendingActionSnackbar: React.FC<PendingActionSnackbarProps> = ({
  open,
  onClose,
  type,
  actionType,
  summary,
}) => {
  const handleClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    onClose();
  };

  const severity: AlertColor = type === "synced" ? "success" : "warning";
  const { title, detail } =
    type === "synced"
      ? getSyncedDetail(actionType, summary)
      : getExpiredDetail(actionType, summary);

  return (
    <Snackbar
      open={open}
      autoHideDuration={5000}
      onClose={handleClose}
      slotProps={{ transition: SlideTransition }}
      // TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert onClose={handleClose} severity={severity} variant="filled" sx={{ width: "100%" }}>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {title}
          </Typography>
          {detail && (
            <Typography variant="body2" sx={{ mt: 0.25, opacity: 0.9 }}>
              {detail}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default PendingActionSnackbar;
